import {
  type AttributeMap,
  type Strings,
  type SupportedVersion,
  template,
  type TemplateStringifier,
  UICorePlugin
} from '@clappr/core'
import {Events} from 'common/types/events'
import {parseTracks} from 'utils/vtt'
import {
  getRecordSubtitlesById,
  SubtitleKey,
  type SubtitlesOption,
  type SubtitlesRequest,
  type SubtitlesResponse,
  type SubtitlesLanguageKeys,
  SUBTITLES_ORDER
} from 'common/api/records'
import type {Options} from 'common/types/options'
import type {MediaControl} from 'common/plugins/media-control'
import {UNKNOWN_NUMBER_ID} from 'common/constants'
import pluginHtml from './template.html'

export class Subtitles<T extends Options> extends UICorePlugin<T> {
  private subtitles: Record<number, SubtitlesResponse> = {}
  private $subtitles!: ZeptoCollection
  private $toggleSubtitles?: ZeptoCollection
  private currentSubtitle: any = null
  private subtitleId = UNKNOWN_NUMBER_ID

  public get name(): string {
    return 'subtitles'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  public get attributes(): AttributeMap {
    return {
      class:
        'eplayer-skin-fullsize eplayer-skin-shiftable eplayer-skin-background',
      style: 'display: none; pointer-events: none',
      'data-subtitles': ''
    }
  }

  public get template(): TemplateStringifier {
    return template(pluginHtml)
  }

  public get i18n(): Strings<T> {
    return this.core.i18n
  }

  private get mediaControl(): MediaControl<T> | null {
    return this.core.getPlugin('mediaControl') as MediaControl<T> | null
  }

  public get currentSubtitles(): SubtitlesResponse {
    return this.subtitles[this.subtitleId] ?? {}
  }

  public get subtitlesList(): SubtitlesOption[] {
    const {subtitles} = this.core.options

    if (!subtitles?.length) {
      return []
    }

    const options = subtitles
      .sort(
        (a, b) =>
          SUBTITLES_ORDER[a.language as SubtitlesLanguageKeys] -
          SUBTITLES_ORDER[b.language as SubtitlesLanguageKeys]
      )
      .map((item) => ({
        id: item.id,
        label: this.i18n.t(item.language)
      }))

    options.unshift({
      id: UNKNOWN_NUMBER_ID,
      label: this.i18n.t(SubtitleKey.DISABLED)
    })

    return options as SubtitlesOption[]
  }

  public bindEvents(): void {
    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.resetEvents
    )

    if (this.mediaControl) {
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_RENDERED,
        this.render
      )
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_SUBTITLE_CHANGED,
        this.updateSubtitles
      )
    }

    const {activeContainer} = this.core

    if (activeContainer) {
      this.listenTo(
        activeContainer,
        Events.CONTAINER_TIMEUPDATE,
        this.renderSubtitles
      )
    }
  }

  public resetEvents(): void {
    this.stopListening()
    this.bindEvents()
  }

  private async getSubtitlesById(
    params: SubtitlesRequest
  ): Promise<SubtitlesResponse> {
    let subtitles = await getRecordSubtitlesById(params)

    if (subtitles) {
      subtitles = {
        ...subtitles,
        file: parseTracks(subtitles.file as string)
      }
    }

    return subtitles ?? ({} as SubtitlesResponse)
  }

  public async updateSubtitles(subtitleId: number): Promise<void> {
    const {playList} = this.core.options

    if (subtitleId && playList) {
      this.subtitleId = subtitleId
      this.$toggleSubtitles?.find('[data-key]').removeClass('active')
      this.$toggleSubtitles
        ?.find(`[data-key="${subtitleId}"]`)
        .addClass('active')

      if (subtitleId === UNKNOWN_NUMBER_ID) {
        this.$toggleSubtitles?.removeClass('eplayer-skin-button-active')
        this.$el.hide()
      } else {
        const id = playList.uuid ?? playList.id
        const idProperty = playList.uuid ? 'recordUuid' : 'recordId'

        const params = {
          [idProperty]: id,
          subtitleId
        }

        this.subtitles[subtitleId] ??= await this.getSubtitlesById(params)

        this.$toggleSubtitles?.addClass('eplayer-skin-button-active')
        this.$el.show()
      }
    }
  }

  public renderSubtitles(event: {current: number}): void {
    const subtitles = this.subtitles?.[this.subtitleId]?.file
    const [current = null] = Array.isArray(subtitles)
      ? subtitles.filter(
          (item) => item.start <= event.current && item.end > event.current
        )
      : []

    if (this.currentSubtitle === current?.id) {
      return
    }

    if (!current) {
      this.$el.hide()
      this.currentSubtitle = null

      return
    }

    this.$subtitles.html(current.text)
    this.$el.show()
    this.currentSubtitle = current
  }

  public renderToggleSubtitles(): void {
    this.$toggleSubtitles = this.mediaControl?.$el.find(
      '.eplayer-skin-subtitle'
    )

    if (this.subtitlesList.length === 0) {
      this.$toggleSubtitles?.remove()

      return
    }

    if (this.$toggleSubtitles?.length === 0) {
      return
    }

    this.subtitlesList.forEach((item) => {
      const $option = $(
        `<span class="subtitle" data-key="${item.id}">{item.label}</span>`
      )

      if (item.id === this.subtitleId) {
        $option.addClass('active')
      }

      this.$toggleSubtitles?.find('.subtitles').append($option)
    })

    this.$toggleSubtitles?.show()
  }

  public render(): this {
    this.$el.html(this.template())
    this.$subtitles = this.$el.find('.eplayer-subtitles p')
    this.$subtitles.css('pointer-events', 'auto')
    this.renderToggleSubtitles()
    this.mediaControl?.$el.append(this.el)

    return this
  }
}
